import {
  Box,
  Button,
  CircularProgress,
  Grid2,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useState } from "react";
import { viewPDF } from "../../../utils/viewPDF";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useNavigate, useParams } from "react-router-dom";
import { uploadFile } from "../../../utils/uploadToAzure";
import InfoIcon from "@mui/icons-material/Info";

const Step2 = ({
  tender,
  qDetails,
  setQDetails,
  setStep,
  storedUser,
  fileNames,
  setFileNames,
}) => {
  const [errors, setErrors] = useState({});
  const [uploadLoading, setUploadLoading] = useState({
    clientReference: false,
    performanceHistory: false,
    certifications: false,
    dataProtection: false,
    securityMeasures: false,
    financialStatements: false,
    bankStatements: false,
    others: false,
  });

  const setFieldLoading = (field, isLoading) => {
    setUploadLoading((prev) => ({
      ...prev,
      [field]: isLoading,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    const data = qDetails || {};
    const isEmpty = (value) => !value || value.trim?.() === "";

    if (isEmpty(data.serviceOffered)) {
      newErrors.serviceOffered = "Products/Services Offered are required";
    } else if (data.serviceOffered.length > 1000) {
      newErrors.serviceOffered =
        "Products/Services Offered should be upto 1000 characters";
    }
    if (isEmpty(data.deliveryTerms)) {
      newErrors.deliveryTerms = "Delivery terms are required";
    } else if (data.deliveryTerms.length > 1000) {
      newErrors.deliveryTerms = "Delivery terms should be upto 1000 characters";
    }
    if (isEmpty(data.paymentTerms)) {
      newErrors.paymentTerms = "Payment terms are required";
    } else if (data.paymentTerms.length > 1000) {
      newErrors.paymentTerms = "Payment terms should be upto 1000 characters";
    }
    // Optional file: only check if error already exists
    if (errors.clientReference)
      newErrors.clientReference = errors.clientReference;
    if (errors.performanceHistory)
      newErrors.performanceHistory = errors.performanceHistory;
    if (errors.certifications) newErrors.certifications = errors.certifications;
    if (errors.dataProtection) newErrors.dataProtection = errors.dataProtection;
    if (errors.securityMeasures)
      newErrors.securityMeasures = errors.securityMeasures;
    if (errors.financialStatements)
      newErrors.financialStatements = errors.financialStatements;
    if (errors.bankStatements) newErrors.bankStatements = errors.bankStatements;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setQDetails({ ...qDetails, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const handleNext = () => {
    if (!validateForm()) return;
    setStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };

  const handleFileUpload = async (e) => {
    const { name, files } = e.target;
    setFieldLoading(name, true);

    if (files && files.length > 0) {
      const file = files[0]; // Get the first file

      // Update the file names state
      setFileNames((prev) => ({
        ...prev,
        [name]: file.name, // Set the filename in state
      }));

      const formData = new FormData();
      formData.append("file", file);

      try {
        const res = await uploadFile(
          file,
          tender.orgId,
          tender.tenderId,
          storedUser
        );
        // Set the received file path in tender state
        setQDetails({ ...qDetails, [e.target.name]: res });
        setErrors({ ...errors, [e.target.name]: "" });
      } catch (error) {
        setErrors({
          ...errors,
          [e.target.name]:
            "Cannot upload file. Make sure file size is less than 1MB",
        });
      } finally {
        setFieldLoading(name, false);
      }
    }
  };

  return (
    <div>
      <Box className="dashboard-container" px={10}>
        <Typography variant="h6">Operational Details</Typography>
        <Grid2 container columnSpacing={2}>
          <Grid2 size={{ xs: 12, sm: 12 }}>
            <label htmlFor="serviceOffered">Products/Service Offered</label>
            <textarea
              id="serviceOffered"
              name="serviceOffered"
              rows={8}
              onChange={handleChange}
              value={qDetails && qDetails.serviceOffered}
              className={`${errors.serviceOffered && "input-error"}`}
            />
            {errors.serviceOffered && (
              <p className="error">{errors.serviceOffered}</p>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12 }}>
            <label htmlFor="deliveryTerms">Delivery Terms</label>
            <textarea
              id="deliveryTerms"
              name="deliveryTerms"
              rows={8}
              onChange={handleChange}
              value={qDetails && qDetails.deliveryTerms}
              className={`${errors.deliveryTerms && "input-error"}`}
            />
            {errors.deliveryTerms && (
              <p className="error">{errors.deliveryTerms}</p>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 12 }}>
            <label htmlFor="paymentTerms">Payment Terms</label>
            <textarea
              id="paymentTerms"
              name="paymentTerms"
              rows={8}
              onChange={handleChange}
              value={qDetails && qDetails.paymentTerms}
              className={`${errors.paymentTerms && "input-error"}`}
            />
            {errors.paymentTerms && (
              <p className="error">{errors.paymentTerms}</p>
            )}
          </Grid2>
        </Grid2>
      </Box>

      <Box className="dashboard-container" px={10}>
        <Typography variant="h6">References and Performance</Typography>
        <Grid2 container columnSpacing={2}>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="clientReference">
              Client References (Client Name, Project, Status)
              <div
                className={`input-file ${
                  errors.clientReference && "input-error"
                }`}
              >
                <input
                  type="file"
                  id="clientReference"
                  name="clientReference"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.clientReference || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            <Box>
              {errors.clientReference && (
                <p className="error">{errors.clientReference}</p>
              )}
              {uploadLoading.clientReference && (
                <span style={{ fontSize: "12px" }}>
                  Uploading File... <CircularProgress size="30px" />
                </span>
              )}
              {qDetails &&
                qDetails.clientReference &&
                !uploadLoading.clientReference && (
                  <Button
                    sx={{
                      margin: "-6% 0 0 0 !important",
                      width: "max-content !important",
                      fontSize: "12px !important",
                      textDecoration: "underline",
                      textAlign: "start",
                    }}
                    onClick={() =>
                      viewPDF(qDetails.clientReference, storedUser)
                    }
                  >
                    {fileNames.clientReference}
                  </Button>
                )}
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="performanceHistory">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: "4px",
                }}
              >
                Performance History
                <Tooltip title={"Completion certificates/feedback letters"}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </div>
              <div
                className={`input-file ${
                  errors.performanceHistory && "input-error"
                }`}
              >
                <input
                  type="file"
                  id="performanceHistory"
                  name="performanceHistory"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.performanceHistory || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            <Box>
              {errors.performanceHistory && (
                <p className="error">{errors.performanceHistory}</p>
              )}
              {uploadLoading.performanceHistory && (
                <span style={{ fontSize: "12px" }}>
                  Uploading File... <CircularProgress size="30px" />
                </span>
              )}
              {qDetails &&
                qDetails.performanceHistory &&
                !uploadLoading.performanceHistory && (
                  <Button
                    sx={{
                      margin: "-6% 0 0 0 !important",
                      width: "max-content !important",
                      fontSize: "12px !important",
                      textDecoration: "underline",
                      textAlign: "start",
                    }}
                    onClick={() =>
                      viewPDF(qDetails.performanceHistory, storedUser)
                    }
                  >
                    {fileNames.performanceHistory}
                  </Button>
                )}
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="certifications">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: "4px",
                }}
              >
                Certifications and Accreditations
                <Tooltip title={"Industry specific licence and certifications"}>
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </div>
              <div
                className={`input-file ${
                  errors.certifications && "input-error"
                }`}
              >
                <input
                  type="file"
                  id="certifications"
                  name="certifications"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.certifications || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            <Box>
              {errors.certifications && (
                <p className="error">{errors.certifications}</p>
              )}
              {uploadLoading.certifications && (
                <span style={{ fontSize: "12px" }}>
                  Uploading File... <CircularProgress size="30px" />
                </span>
              )}
              {qDetails &&
                qDetails.certifications &&
                !uploadLoading.certifications && (
                  <Button
                    sx={{
                      margin: "-6% 0 0 0 !important",
                      width: "max-content !important",
                      fontSize: "12px !important",
                      textDecoration: "underline",
                      textAlign: "start",
                    }}
                    onClick={() => viewPDF(qDetails.certifications, storedUser)}
                  >
                    {fileNames.certifications}
                  </Button>
                )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>

      <Box className="dashboard-container" px={10}>
        <Typography variant="h6">Security and Data Protection</Typography>
        <Grid2 container columnSpacing={2}>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="dataProtection">
              Data Protection Policies
              <div
                className={`input-file ${
                  errors.dataProtection && "input-error"
                }`}
              >
                <input
                  type="file"
                  id="dataProtection"
                  name="dataProtection"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.dataProtection || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            <Box>
              {errors.dataProtection && (
                <p className="error">{errors.dataProtection}</p>
              )}
              {uploadLoading.dataProtection && (
                <span style={{ fontSize: "12px" }}>
                  Uploading File... <CircularProgress size="30px" />
                </span>
              )}
              {qDetails &&
                qDetails.dataProtection &&
                !uploadLoading.dataProtection && (
                  <Button
                    sx={{
                      margin: "-6% 0 0 0 !important",
                      width: "max-content !important",
                      fontSize: "12px !important",
                      textDecoration: "underline",
                      textAlign: "start",
                    }}
                    onClick={() => viewPDF(qDetails.dataProtection, storedUser)}
                  >
                    {fileNames.dataProtection}
                  </Button>
                )}
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="securityMeasures">
              Security Measures in Place
              <div
                className={`input-file ${
                  errors.securityMeasures && "input-error"
                }`}
              >
                <input
                  type="file"
                  id="securityMeasures"
                  name="securityMeasures"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.securityMeasures || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            <Box>
              {errors.securityMeasures && (
                <p className="error">{errors.securityMeasures}</p>
              )}
              {uploadLoading.securityMeasures && (
                <span style={{ fontSize: "12px" }}>
                  Uploading File... <CircularProgress size="30px" />
                </span>
              )}
              {qDetails &&
                qDetails.securityMeasures &&
                !uploadLoading.securityMeasures && (
                  <Button
                    sx={{
                      margin: "-6% 0 0 0 !important",
                      width: "max-content !important",
                      fontSize: "12px !important",
                      textDecoration: "underline",
                      textAlign: "start",
                    }}
                    onClick={() =>
                      viewPDF(qDetails.securityMeasures, storedUser)
                    }
                  >
                    {fileNames.securityMeasures}
                  </Button>
                )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>

      <Box className="dashboard-container" px={10}>
        <Typography variant="h6">Additional Documentation</Typography>
        <Grid2 container columnSpacing={2}>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="financialStatements">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: "4px",
                }}
              >
                Audited Financial Statements
                <Tooltip
                  title={"Auditors report, balance sheet, income statement"}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </div>
              <div
                className={`input-file ${
                  errors.financialStatements && "input-error"
                }`}
              >
                <input
                  type="file"
                  id="financialStatements"
                  name="financialStatements"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.financialStatements || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            <Box>
              {errors.financialStatements && (
                <p className="error">{errors.financialStatements}</p>
              )}
              {uploadLoading.financialStatements && (
                <span style={{ fontSize: "12px" }}>
                  Uploading File... <CircularProgress size="30px" />
                </span>
              )}
              {qDetails &&
                qDetails.financialStatements &&
                !uploadLoading.financialStatements && (
                  <Button
                    sx={{
                      margin: "-6% 0 0 0 !important",
                      width: "max-content !important",
                      fontSize: "12px !important",
                      textDecoration: "underline",
                      textAlign: "start",
                    }}
                    onClick={() =>
                      viewPDF(qDetails.financialStatements, storedUser)
                    }
                  >
                    {fileNames.financialStatements}
                  </Button>
                )}
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="bankStatements">
              Bank Statements
              <div
                className={`input-file ${
                  errors.bankStatements && "input-error"
                }`}
              >
                <input
                  type="file"
                  id="bankStatements"
                  name="bankStatements"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.bankStatements || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            <Box>
              {errors.bankStatements && (
                <p className="error">{errors.bankStatements}</p>
              )}
              {uploadLoading.bankStatements && (
                <span style={{ fontSize: "12px" }}>
                  Uploading File... <CircularProgress size="30px" />
                </span>
              )}
              {qDetails &&
                qDetails.bankStatements &&
                !uploadLoading.bankStatements && (
                  <Button
                    sx={{
                      margin: "-6% 0 0 0 !important",
                      width: "max-content !important",
                      fontSize: "12px !important",
                      textDecoration: "underline",
                      textAlign: "start",
                    }}
                    onClick={() => viewPDF(qDetails.bankStatements, storedUser)}
                  >
                    {fileNames.bankStatements}
                  </Button>
                )}
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="others">
              Any other relevant documents as per company policy
              <div className={`input-file ${errors.others && "input-error"}`}>
                <input
                  type="file"
                  id="others"
                  name="others"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.others || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            <Box>
              {errors.others && <p className="error">{errors.others}</p>}
              {uploadLoading.others && (
                <span style={{ fontSize: "12px" }}>
                  Uploading File... <CircularProgress size="30px" />
                </span>
              )}
              {qDetails && qDetails.others && !uploadLoading.others && (
                <Button
                  sx={{
                    margin: "-6% 0 0 0 !important",
                    width: "max-content !important",
                    fontSize: "12px !important",
                    textDecoration: "underline",
                    textAlign: "start",
                  }}
                  onClick={() => viewPDF(qDetails.others, storedUser)}
                >
                  {fileNames.others}
                </Button>
              )}
            </Box>
          </Grid2>
        </Grid2>
      </Box>

      <Box className="btn-container" sx={{ gap: 5 }}>
        <Button className="btn-outlined" sx={{ px: 7 }} onClick={handleBack}>
          Back
        </Button>
        <Button className="btn-contained" sx={{ px: 7 }} onClick={handleNext}>
          Next
        </Button>
      </Box>
    </div>
  );
};

export default Step2;
